import { memo, useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import _ from 'lodash';
import {
  changeRelationType,
  checkRelationTypeSaveDisabled,
  deleteRelationType,
  firstRelationTypeSelector,
  relationTypeMapSelector,
  secondRelationTypeSelector,
  setFirstRelationType,
  setSecondRelationType,
  upsertRelationType,
} from 'slices/relationType.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { IProps } from 'types/props.interface';
import type { DrawerCallback } from 'types/utils.type';
import type { TRelationType } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import RelationType from './RelationType';

const RelationTypeDrawer = ({ confirmDiscardChanges }: IProps) => {
  const dispatch = useAppDispatch();

  const firstRelationType: TRelationType = useAppSelector(
    firstRelationTypeSelector
  );
  const secondRelationType: TRelationType = useAppSelector(
    secondRelationTypeSelector
  );
  const relationTypeMapping: Record<number | string, TRelationType> =
    useAppSelector(relationTypeMapSelector);

  const isFirstRelationTypeSaveDisabled = useMemo(
    () => dispatch(checkRelationTypeSaveDisabled(true)),
    [firstRelationType]
  );

  const isSecondRelationTypeSaveDisabled = useMemo(
    () => dispatch(checkRelationTypeSaveDisabled(false)),
    [secondRelationType]
  );

  const setFirstResource = useCallback(
    (relationType: TRelationType) =>
      dispatch(setFirstRelationType(relationType)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (relationType: TRelationType) =>
      dispatch(setSecondRelationType(relationType)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    relationTypeMapping,
    firstRelationType,
    secondRelationType,
    setFirstResource,
    setSecondResource
  );

  const onClose = (isFirstDrawer: boolean) => {
    confirmDiscardChanges(isFirstDrawer, () => {
      if (isFirstDrawer) {
        dispatch(setFirstRelationType(null));
      } else {
        dispatch(setSecondRelationType(null));
      }
    });
  };

  const onChange: DrawerCallback<TRelationType> = (
    isFirstRelationType,
    property,
    value
  ) => {
    dispatch(changeRelationType({ isFirstRelationType, property, value }));
  };

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: _.get(firstRelationType, 'name', ''),
        content: (
          <RelationType
            relationType={firstRelationType}
            onChange={(key, value) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstRelationType,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('relation_type_confirmation_delete')}
            handleDelete={() =>
              dispatch(deleteRelationType(firstRelationType.id))
            }
            handleDiscard={() =>
              dispatch(
                setFirstRelationType(relationTypeMapping[firstRelationType.id])
              )
            }
            handleSave={() => dispatch(upsertRelationType(firstRelationType!))}
            isSaveDisable={isFirstRelationTypeSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => {
          if (!open) onClose(true);
        },
      }}
      secondDrawer={{
        title: _.get(secondRelationType, 'name', ''),
        content: (
          <RelationType
            relationType={secondRelationType}
            onChange={(key, value) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondRelationType,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('relation_type_confirmation_delete')}
            handleDelete={() =>
              dispatch(deleteRelationType(secondRelationType.id))
            }
            handleDiscard={() =>
              dispatch(
                setSecondRelationType(
                  relationTypeMapping[secondRelationType.id]
                )
              )
            }
            handleSave={() => dispatch(upsertRelationType(secondRelationType!))}
            isSaveDisable={isSecondRelationTypeSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => {
          if (!open) onClose(false);
        },
      }}
      onExpand={() => null}
    />
  );
};

export default memo(RelationTypeDrawer);
